import React, { createContext, useState, useEffect } from 'react'
import netlifyIdentity from 'netlify-identity-widget'

const AuthContext = createContext({
  user: null,
  userRoles: [],
  authReady: false,
  login: () => {},
  logout: () => {},
  refresh: () => {},
  hasRole: () => {},
  isAdmin: () => {},
  isSuspended: () => {},
  getToken: () => {},
  getRequest: () => {},
  postRequest: () => {},
  setError: () => {}
})

export const AuthContextProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userRoles, setUserRoles] = useState(null)
  const [authReady, setAuthReady] = useState(false)
  const [error, setError] = useState(false)

  useEffect(() => {
    netlifyIdentity.on('login', user => {
      setUser(user)
      setUserRoles(user.app_metadata.roles)
      netlifyIdentity.close()
      // console.log('login event')
    })
    netlifyIdentity.on('logout', () => {
      setUser(null)
      setError(null)
      // console.log('logout event')
    })
    netlifyIdentity.on('init', user => {
      if (user) {
        if (Date.now() > user.token.expires_at) {
          refresh()
        } else {
          setUser(user)
          setUserRoles(user.app_metadata.roles)
        }
      }
      setAuthReady(true)
      // console.log('init event')
    })

    netlifyIdentity.init()

    return () => {
      netlifyIdentity.off('login')
    }
  }, [])

  const login = () => {
    netlifyIdentity.open()
  }

  const logout = () => {
    netlifyIdentity.logout()
  }

  const refresh = async () => {
    const updatedUser = await netlifyIdentity.currentUser().update({})
    setUser(updatedUser)
    setUserRoles(updatedUser.app_metadata.roles)
  }

  const getToken = async () => {
    const currentUser = netlifyIdentity.currentUser()
    if (!currentUser) {
      return null
    }
    // fetches new JWT token only if expired
    await currentUser.jwt()
    return currentUser.token.access_token
  }

  const hasRole = role => user && userRoles?.includes(role?.toUpperCase())

  const isAdmin = () => user && userRoles?.includes('ADMIN')
  const isSuspended = () => user && userRoles?.includes('SUSPENDED')

  const getRequest = async url => {
    setError(null)
    const token = await getToken()
    return fetch(url, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(async response => {
      const json = await response.json()
      return {
        status: response.status === 200 ? true : false,
        msg: json.msg
      }
    })
  }

  const postRequest = async (url, body) => {
    setError(null)
    const token = await getToken()
    return fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(body)
    }).then(async response => {
      const json = await response.json()
      return {
        status: response.status === 200 ? true : false,
        msg: json.msg
      }
    })
  }

  const context = {
    user,
    userRoles,
    login,
    logout,
    refresh,
    hasRole,
    isAdmin,
    isSuspended,
    authReady,
    getToken,
    getRequest,
    postRequest,
    error,
    setError
  }

  return <AuthContext.Provider value={context}>{children}</AuthContext.Provider>
}

export default AuthContext
